import { z } from 'zod';
// Asset event vocabulary — single source of truth for `asset_events.event_type`
// (vocabulary in code, NO DB CHECK — the audit_logs.action precedent: a future
// verb (transit, warehouse ceremony, tickets-era) is a code change, never a
// migration).
//
// The split test (ADR-063 as revised at the datamodel session): an event row
// exists ONLY for a real-world lifecycle moment (effective-dated; consumed by
// the History timeline, the finance digest's period movements, the period
// activity report, pairing). Record-only acts — the `edit` verb — write
// audit_logs ALONE (kind 'edited' carries the diff). The remaining overlap
// (a move writes both stores) is the deliberate two-tier pattern (ADR-041):
// forensic envelope in audit, typed domain ledger in asset_events.
// One act → ONE row per touched asset, typed by what happened to THAT asset
// (architect-ruled — ADR-063). A swap therefore writes `replaced` on the
// outgoing and — when the incoming is born right there — `created` (carrying
// counterpart_asset_id) on the newborn, never a created+replaced double row.
// The composer doors (decommission / dispose) may ride several rungs in one
// act (verdict → write_off → dispose); every rung is its own row, all sharing
// one correlation_id.
export const ASSET_EVENT_TYPES = [
    'created', // birth into the register, wherever it physically is (counterpart set when born as a replacement)
    'moved', // location change (one event per asset; cascades share correlation; a retired unit relocates status-preserving)
    'replaced', // the swap composite, per touched asset (counterpart_asset_id = the other)
    'adjusted', // aggregate unit_count delta (unit_count_delta)
    'put_on_hold', // in-place: deployed/stocked → on_hold (no verdict — the undecided state)
    'returned_to_service', // in-place: on_hold → deployed (site) / stocked (warehouse)
    'retired', // the end-of-life VERDICT (the graveyard is real); may carry a pull-out location pair
    'reinstated', // verdict reversal: retired → back to the living fleet (defaults ON HOLD — triage; stocked/deployed explicit); blocked once disposed; books facts stand
    'lost', // can't be found; keeps last known location, flagged
    'recovered', // a lost thing turned up (restores the pre-loss status, retired included)
    'written_off', // books: value killed (asset.books:write)
    'disposed', // OPS: the physical exit — scrapped/sold/donated/returned; location clears; the books read it
    'ownership_transferred', // books: owner change (from/to_owner_id)
];
export const AssetEventTypeSchema = z.enum(ASSET_EVENT_TYPES);
// The condition vocabulary — the register's 4-grade verdict. Rides the event
// condition axis (from_condition/to_condition) and the assets row columns;
// the category charter holds the words (ADR-067). Vocabulary in code.
export const ASSET_CONDITION_GRADES = ['good', 'fair', 'poor', 'failed'];
export const AssetConditionGradeSchema = z.enum(ASSET_CONDITION_GRADES);
export const ASSET_EVENT_AXES = {
    created: ['location', 'status', 'condition'],
    moved: ['location', 'status'],
    replaced: ['location', 'status', 'condition'],
    adjusted: ['count'],
    put_on_hold: ['status', 'condition'],
    returned_to_service: ['status', 'condition'],
    retired: ['location', 'status', 'condition'],
    reinstated: ['location', 'status'],
    lost: ['status'],
    recovered: ['location', 'status'],
    written_off: ['books'],
    disposed: ['location', 'books'],
    ownership_transferred: ['books'],
};
// Normalized reason vocabulary for `asset_events.reason_type` — the filterable
// "why" beside the free-text note (the period activity report filters on it).
// Starter set; grows beside the verb that needs a new one (code change only).
export const ASSET_EVENT_REASON_TYPES = [
    'failed',
    'damaged',
    'obsolete',
    'upgrade',
    'maintenance',
    'awaiting_spares',
    'standby',
    'theft_suspected',
    'not_found',
    'found',
    'expansion',
    'site_decommission',
    'swap',
    'repaired', // the fault was fixed (decide-release after repair)
    'refurbished', // recommission / release: overhauled and back
    'inspection_passed', // the reception blessing: checked, nothing repaired (release to stock)
    'beyond_repair', // the retire rationale distinct from the fault itself
    'stock_rebalance', // depot ↔ depot moves that are neither expansion nor swap
    'count_correction', // adjust reconciling a physical count (the consumables-ledger twin)
    'suspected_fault', // faulty-door suspicion lane: symptom discovered, grade unasserted (note carries it)
    'recorded_in_error', // reversal of a mis-recorded act (fat-finger retire/lost)
    'date_correction',
    'other',
];
export const AssetEventReasonTypeSchema = z.enum(ASSET_EVENT_REASON_TYPES);
